import React, { ReactNode } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Box, Card, CardContent, Stack, useTheme } from '@mui/material'
import { DataGridPro, gridClasses, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import useMediaQuery from '@mui/system/useMediaQuery'
import { getMuiDataGridLocaleText } from '../../i18n/localeUtils'
import DateGroupHeader from './DateGroupHeader'
import DateGroupItem from './DateGroupItem'
import NewsHeadlinePublication from './NewsHeadlinePublication'
import NewsHeadlineTerms from './NewsHeadlineTerms'
import NewsHeadlineOutcome from './NewsHeadlineOutcome'
import NewsHeadlineProspectusMemorandum from './NewsHeadlineProspectusMemorandum'
import { NewsItem } from '../../types/NewsItem'
import { NewsHeadlineConversion } from './NewsHeadlineConversion'

export type EnrichedNewsItem = NewsItem & {
    tempId: string
    hierarchy: string[]
    id: number
}

type NewsItemsGridComponent = React.FC<{
    newsItems: EnrichedNewsItem[]
}>

const disableSelectedSellOutlineStyle = {
    [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
        outline: 'none',
    },
    [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
        outline: 'none',
    },
}

const getNewsGridLocaleText = (locale: string | undefined, noRowsLabel: string) =>
    getMuiDataGridLocaleText(locale, { noRowsLabel })

const addDateGroups = (items: EnrichedNewsItem[]) => {
    const groups: EnrichedNewsItem[] = items.map((item, index) => {
        return {
            ...item,
            id: index + 1,
            hierarchy: [item.publicationDate, item.symbol + '-' + item.tempId].filter((v) => v !== null),
        }
    })

    groups.sort((a, b) => new Date(b.publicationDate!).getTime() - new Date(a.publicationDate!).getTime())

    return groups
}

const wrapLink = (entityId: string, entityType: string, children: ReactNode) => (
    <Link
        style={{
            overflowWrap: 'break-word',
            whiteSpace: 'normal',
            maxHeight: 'none',
        }}
        to={`/${entityType === 'Warrant' ? 'warrants' : entityType === 'Convertible' ? 'convertibles' : 'issues'}/${entityId}`}
    >
        {children}
    </Link>
)

const Headline = (params: GridRenderCellParams<EnrichedNewsItem>) => {
    const { t, i18n } = useTranslation()
    const locale = i18n.language

    if (params.value === 'publication' || params.row?.issueData?.type === 'Guarantor fee issue') {
        return wrapLink(params.row.entityId, params.row.entityType, <NewsHeadlinePublication row={params.row} />)
    } else if (params.value === 'conversion') {
        return wrapLink(params.row.entityId, params.row.entityType, <NewsHeadlineConversion row={params.row} />)
    } else if (params.value === 'terms') {
        return wrapLink(params.row.entityId, params.row.entityType, <NewsHeadlineTerms row={params.row} />)
    } else if (params.value === 'outcome' || params.value === 'preliminary outcome') {
        return wrapLink(
            params.row.entityId,
            params.row.entityType,
            <NewsHeadlineOutcome row={params.row} locale={locale} />,
        )
    } else if (['prospectus', 'memorandum', 'informationdocument'].includes(params.value)) {
        return wrapLink(
            params.row.entityId,
            params.row.entityType,
            <NewsHeadlineProspectusMemorandum row={params.row} />,
        )
    } else if (params.value === 'cancellation') {
        if (params.row?.issueData?.type === 'Non-cash issue') {
            return wrapLink(
                params.row.entityId,
                params.row.entityType,
                t('pages.news.headlines.cancellation.nonCash', {
                    companyName: params.row.companyName,
                    issueType: t(`entities.issueType.values.${params.row?.issueData.type}`).toLowerCase(),
                    consideration: params.row?.issueData?.noncashConsideration || '?',
                }),
            )
        } else {
            return wrapLink(
                params.row.entityId,
                params.row.entityType,
                t('pages.news.headlines.cancellation.default', {
                    companyName: params.row.companyName,
                    issueType: t(`entities.issueType.values.${params.row.issueData.type}`).toLowerCase(),
                }),
            )
        }
    } else if (params.value === 'new commitments') {
        if (params.row.entityType === 'Warrant') {
            return wrapLink(
                params.row.entityId,
                params.row.entityType,
                t('pages.news.headlines.newCommitments.warrant', {
                    companyName: params.row.companyName,
                    name: params.row.name || params.row.warrantData.name,
                }),
            )
        }
        return wrapLink(
            params.row.entityId,
            params.row.entityType,
            t('pages.news.headlines.newCommitments.issue', {
                companyName: params.row.companyName,
                issueType: t(`entities.issueType.values.${params.row.issueData.type}`).toLowerCase(),
            }),
        )
    }
}

const ListViewColumn = (params: GridRenderCellParams<EnrichedNewsItem>) => {
    const { rowNode } = params
    const isGroup = rowNode.type === 'group'

    if (isGroup)
        return (
            <div
                style={{
                    marginTop: 20,
                }}
            >
                <DateGroupHeader {...params} />
            </div>
        )

    return (
        <div
            style={{
                padding: 10,
            }}
        >
            <Stack
                direction='row'
                spacing={2}
                sx={{
                    alignItems: 'start',
                    display: 'flex',
                }}
            >
                <span style={{ minWidth: 70 }}>
                    <DateGroupItem {...params} isSlim />
                </span>
                <Headline {...params} />
            </Stack>
        </div>
    )
}

export const NewsItemsGrid: NewsItemsGridComponent = ({ newsItems }) => {
    const { i18n, t } = useTranslation()
    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))
    const locale = i18n.language
    const rows = addDateGroups(newsItems)

    const columns: GridColDef<EnrichedNewsItem>[] = [
        {
            field: 'eventType',
            flex: 1,
            display: 'flex',
            sortable: false,
            renderCell: (params) => <Headline {...params} />,
        },
    ]

    return (
        <Card sx={{ marginBottom: 6 }}>
            <CardContent>
                <Box sx={{ width: '100%' }}>
                    <DataGridPro
                        columnHeaderHeight={0}
                        sx={{
                            ...disableSelectedSellOutlineStyle,
                            '& .MuiDataGrid-row--borderBottom .MuiDataGrid-columnHeader': {
                                borderBottom: 0,
                            },
                            '.MuiDataGrid-cell': {
                                whiteSpace: 'normal',
                                wordWrap: 'break-word',
                                lineHeight: '1.5',
                                border: 'none',
                                minHeight: '40px',
                            },
                            '.MuiDataGrid-virtualScroller': { overflow: 'hidden' },
                            border: 'none',
                        }}
                        defaultGroupingExpansionDepth={-1}
                        treeData
                        disableColumnSorting
                        disableColumnResize
                        disableColumnReorder
                        disableColumnFilter
                        disableColumnMenu
                        disableRowSelectionOnClick
                        listView={isSmallScreen}
                        listViewColumn={{
                            field: 'eventType',
                            renderCell: (params) => <ListViewColumn {...params} />,
                        }}
                        rows={rows}
                        columns={columns}
                        getTreeDataPath={(row) => row.hierarchy}
                        getRowId={(row) => row.id}
                        getRowHeight={() => 'auto'}
                        groupingColDef={{
                            minWidth: 240,
                            renderCell: (params) => {
                                const { rowNode } = params
                                const isGroup = rowNode.type === 'group'

                                // render group header or item, i.e. date or news item
                                return isGroup ? <DateGroupHeader {...params} /> : <DateGroupItem {...params} />
                            },
                        }}
                        localeText={getNewsGridLocaleText(locale, t('pages.news.noRows'))}
                        density='compact'
                    />
                </Box>
            </CardContent>
        </Card>
    )
}
